/** Read-only display quotes from the same endpoint used by checkout. */
export type DeliveryQuote = { feePence: number | null; outOfArea?: boolean };
export type DeliveryFeeFields = { offersDelivery?: boolean | null; hasDeliveryZones?: boolean | null; deliveryFeePence?: number | null; deliveryTimeMins?: number | null; deliveryFeeLabel?: string; quotedDeliveryFeePence?: number; deliveryUnavailable?: boolean };
export const normaliseDeliveryPostcode = (postcode?: string | null) => (postcode ?? "").replace(/\s/g, "").toUpperCase();
let active = 0;
const waiting: Array<() => void> = [];
async function acquire() { if (active >= 4) await new Promise<void>(resolve => waiting.push(resolve)); else active++; }
function release() { const next = waiting.shift(); if (next) next(); else active--; }
export async function requestDeliveryFee(storeId: number, postcode: string, signal?: AbortSignal, base = "", fetcher: typeof fetch = fetch): Promise<DeliveryQuote> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener("abort", abort, { once: true });
  if (signal?.aborted) abort();
  const timer = setTimeout(abort, 8000);
  await acquire();
  try {
    if (controller.signal.aborted) { const error = new Error("Delivery quote cancelled"); error.name = "AbortError"; throw error; }
    const response = await fetcher(`${base}/api/order/stores/${storeId}/delivery-fee`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ customerPostcode: normaliseDeliveryPostcode(postcode) }), signal: controller.signal });
    if (!response.ok) throw new Error("Delivery quote unavailable");
    const result = await response.json() as DeliveryQuote;
    if (result.outOfArea === true) return { feePence: null, outOfArea: true };
    if (!Number.isSafeInteger(result.feePence) || result.feePence! < 0) throw new Error("Invalid delivery quote");
    return { feePence: result.feePence, outOfArea: false };
  } finally { clearTimeout(timer); signal?.removeEventListener("abort", abort); release(); }
}
export function withDeliveryFee<T extends DeliveryFeeFields>(store: T, postcode: string, quote?: DeliveryQuote, error = false): T & DeliveryFeeFields {
  const result = { ...store, quotedDeliveryFeePence: undefined as number | undefined, deliveryUnavailable: false, deliveryFeeLabel: "" };
  if (!store.offersDelivery) return result;
  if (store.hasDeliveryZones) {
    if (!postcode) result.deliveryFeeLabel = "Add postcode for delivery fee";
    else if (quote?.outOfArea) { result.deliveryUnavailable = true; result.deliveryFeeLabel = "Delivery unavailable to this postcode"; }
    else if (quote && Number.isSafeInteger(quote.feePence) && quote.feePence! >= 0) result.quotedDeliveryFeePence = quote.feePence!;
    else result.deliveryFeeLabel = error ? "Delivery fee unavailable" : "Checking delivery fee…";
  }
  if (!result.deliveryFeeLabel) {
    const fee = result.quotedDeliveryFeePence ?? store.deliveryFeePence;
    result.deliveryFeeLabel = Number.isSafeInteger(fee) && fee! >= 0 ? fee === 0 ? "Free delivery" : `£${(fee! / 100).toFixed(2)} delivery` : "Delivery fee unavailable";
  }
  return result;
}
export function deliveryLine(store: DeliveryFeeFields, minutes?: number | null): string | null {
  if (!store.offersDelivery) return null;
  const label = store.deliveryFeeLabel || withDeliveryFee(store, "").deliveryFeeLabel;
  return store.deliveryUnavailable ? label! : `${minutes ?? store.deliveryTimeMins ?? 0} min · ${label}`;
}
